/**
 * Layout of the admin-uploaded "package takeoff" Excel template (Settings → design templates, file
 * package-takeoff.xlsx): which sheet receives the material table, the first data cell, the ordered columns, whether
 * rows are inserted (pushing the template footer down) and whether the technical-check sheet is appended.
 * General information is written through `{{key}}` placeholders anywhere in the workbook (TAKEOFF_PLACEHOLDERS).
 * Used by the API (validation / storage) and the design app (filling the workbook).
 */
import { z } from 'zod';

export const TAKEOFF_FIELDS = ['stt', 'group', 'code', 'name', 'model', 'specs', 'manufacturer', 'unit', 'quantity', 'note', 'blank'] as const;
export type TakeoffField = (typeof TAKEOFF_FIELDS)[number];

export const TAKEOFF_FIELD_LABEL: Record<TakeoffField, string> = {
  stt: 'STT',
  group: 'Nhóm vật tư',
  code: 'Mã vật tư',
  name: 'Tên vật tư',
  model: 'Model',
  specs: 'Thông số',
  manufacturer: 'Hãng',
  unit: 'ĐVT',
  quantity: 'Số lượng',
  note: 'Ghi chú',
  blank: '(Để trống cột)',
};

/** `{{key}}` placeholders replaced in every sheet of the template. */
export const TAKEOFF_PLACEHOLDERS: Record<string, string> = {
  nhan_su: 'Nhân sự lập',
  ma_kh: 'Mã khách hàng',
  ten_kh: 'Tên khách hàng',
  khach_hang: 'Mã - tên khách hàng',
  ma_goi: 'Mã gói',
  ten_goi: 'Tên gói',
  phien_ban: 'Phiên bản gói',
  loai_he_thong: 'Loại hệ thống',
  cong_suat_kwp: 'Công suất danh định (kWp)',
  bien_tan: 'Biến tần đang dùng',
  pin_luu_tru: 'Pin lưu trữ chọn thêm (tên × số lượng)',
  ban_ve: 'Bản vẽ mẫu',
  ngay: 'Ngày lập',
};

/** Excel cell address such as A12 (column A–XFD, row 1–1048576). */
export const CELL_RE = /^([A-Z]{1,3})([1-9]\d{0,6})$/;

const startCellSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(CELL_RE, 'Ô bắt đầu dạng A12');
const columnsSchema = z.array(z.enum(TAKEOFF_FIELDS)).min(1, 'Chọn ít nhất một cột').max(40);

/** Product category whose lines go to the separate "electrical cabinet" table / sheet. */
export const CABINET_CATEGORY_CODE = 'CABINET';
export const CABINET_SHEET_DEFAULT = 'Vật tư tủ điện';

/** Electrical-cabinet table: own sheet, first data cell, columns (missing sheet in the template ⇒ built-in sheet). */
export const cabinetLayoutSchema = z.object({
  sheet: z.string().trim().min(1, 'Nhập tên trang tính').max(31).default(CABINET_SHEET_DEFAULT),
  startCell: startCellSchema.default('A5'),
  columns: columnsSchema.default(['stt', 'code', 'name', 'model', 'specs', 'manufacturer', 'unit', 'quantity', 'note']),
  insertRows: z.boolean().default(true),
});
export type CabinetLayout = z.infer<typeof cabinetLayoutSchema>;

export const takeoffLayoutSchema = z.object({
  /** Sheet name; empty ⇒ first sheet */
  sheet: z.string().trim().max(31).default(''),
  /** Top-left cell of the first data row (first column of the table) */
  startCell: startCellSchema,
  columns: columnsSchema,
  /** true ⇒ insert rows (copying the style of the first data row) so content below the table moves down */
  insertRows: z.boolean().default(true),
  /** Append the "Kiểm tra kỹ thuật" sheet (warnings & technical requirements) */
  checksSheet: z.boolean().default(true),
  /** Electrical cabinet & protection devices (category CABINET) — separate sheet */
  cabinet: cabinetLayoutSchema.default(() => cabinetLayoutSchema.parse({})),
});
export type TakeoffLayout = z.infer<typeof takeoffLayoutSchema>;

export const DEFAULT_TAKEOFF_LAYOUT: TakeoffLayout = {
  sheet: '',
  startCell: 'A12',
  columns: ['stt', 'group', 'code', 'name', 'model', 'specs', 'manufacturer', 'unit', 'quantity', 'note'],
  insertRows: true,
  checksSheet: true,
  cabinet: cabinetLayoutSchema.parse({}),
};

/** Column letters → 1-based index (A ⇒ 1, AA ⇒ 27). */
export function columnIndex(letters: string): number {
  return [...letters].reduce((n, c) => n * 26 + c.charCodeAt(0) - 64, 0);
}

// ---------------------------------------------------------------- drawing template suggestion
/** Drawing families of the DWG templates (<FAMILY>-1P.dwg / <FAMILY>-3P.dwg). */
export const DWG_FAMILIES = ['ONGRID', 'HYBRID'] as const;
export type DwgFamily = (typeof DWG_FAMILIES)[number];
export const DWG_FAMILY_LABEL: Record<DwgFamily, string> = { ONGRID: 'Hòa lưới (ONGRID-1P / 3P)', HYBRID: 'Hybrid lưu trữ (HYBRID-1P / 3P)' };

/** Admin mapping "system type code → drawing family" (null = no template for that type). */
export const dwgFamilyMapSchema = z.record(z.string().trim().min(1).max(40), z.enum(DWG_FAMILIES).nullable());
export type DwgFamilyMap = z.infer<typeof dwgFamilyMapSchema>;

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/gi, 'd')
    .toLowerCase();

/** Guess from the system type code / name when the admin has not mapped it. */
export function guessDwgFamily(code: string, label: string): DwgFamily | null {
  const t = fold(`${code} ${label}`);
  if (/hybrid|luu tru|storage|ess\b/.test(t)) return 'HYBRID';
  if (/on-?grid|hoa luoi|bam tai/.test(t)) return 'ONGRID';
  return null;
}

/** Drawing family of a system type: admin mapping first (null = explicitly none), otherwise the guess. */
export function dwgFamilyOf(code: string, label: string, map: DwgFamilyMap): { family: DwgFamily | null; source: 'MAP' | 'GUESS' } {
  return code in map ? { family: map[code] ?? null, source: 'MAP' } : { family: guessDwgFamily(code, label), source: 'GUESS' };
}

/** Phase count from an inverter name / model when techSpecs do not declare it ("3P", "1 pha", "-3PH", "three phase"…). */
export function inferPhases(...texts: (string | null | undefined)[]): 1 | 3 | null {
  const t = fold(texts.filter(Boolean).join(' '));
  const three = /(^|[^0-9a-z.])3\s*(p|ph|pha|phase)(?![a-z])|three[\s-]*phase|ba pha/.test(t);
  const one = /(^|[^0-9a-z.])1\s*(p|ph|pha|phase)(?![a-z])|single[\s-]*phase|mot pha/.test(t);
  return three === one ? null : three ? 3 : 1;
}
